import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Field,
  IconButton,
  Panel,
  Select,
  Sheet,
  SimpleTable,
  Switch,
  Tag,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiCreditsApi, type AiEffort, type AiFeatureSettingDto, type PlatformAiDto } from "../../../api/aiCreditsApi";
import { errorMessage, fmtCredits, fmtDate, fmtUsd } from "./creditsUi";

type Tenant = PlatformAiDto["tenants"][number];
type Purchase = PlatformAiDto["purchases"][number];

const EFFORTS: Array<{ value: string; label: string }> = [
  { value: "", label: "ברירת מחדל" },
  { value: "low", label: "נמוך (מהיר וזול)" },
  { value: "medium", label: "בינוני" },
  { value: "high", label: "גבוה" },
  { value: "xhigh", label: "גבוה מאוד" },
  { value: "max", label: "מקסימלי" },
];

/** Edit one feature's route: on/off, model, effort, the models users may pick, monthly platform budget. */
function FeatureDialog({
  feature,
  models,
  onClose,
  onSaved,
}: {
  feature: AiFeatureSettingDto;
  models: PlatformAiDto["models"];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useCkToast();
  const [enabled, setEnabled] = useState(feature.enabled);
  const [model, setModel] = useState(feature.model ?? "");
  const [effort, setEffort] = useState<string>(feature.effort ?? "");
  const [userChoice, setUserChoice] = useState(feature.userChoice);
  const [allowed, setAllowed] = useState<string[]>(feature.allowedModels);
  const [budget, setBudget] = useState(feature.monthlyBudgetUsd === null ? "" : String(feature.monthlyBudgetUsd));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const b = budget.trim();
    if (b && !(Number(b) >= 0)) {
      toast.error("תקציב לא תקין");
      return;
    }
    setBusy(true);
    try {
      await aiCreditsApi.platform.updateFeature(feature.feature, {
        enabled,
        model: model || null,
        effort: (effort || null) as AiEffort | null,
        allowedModels: userChoice ? allowed : [],
        userChoice,
        monthlyBudgetUsd: b ? Number(b) : null,
      });
      toast.success("נשמר");
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`ניתוב — ${feature.label}`}
      icon="sparkle"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={busy} onClick={() => void save()}>
            שמירה
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Switch checked={enabled} onChange={setEnabled} label="פעיל" hint="כבוי = הסוכן לא זמין באף ארגון" />
        <Field label="מודל">
          <Select
            aria-label="מודל"
            value={model}
            onChange={setModel}
            options={[{ value: "", label: "ברירת המחדל של השרת" }, ...models.map((m) => ({ value: m.id, label: m.label }))]}
          />
        </Field>
        <Field label="רמת מאמץ" hint="נמוך = תשובות מהירות וזולות; גבוה = יסודי ויקר יותר">
          <Select aria-label="רמת מאמץ" value={effort} onChange={setEffort} options={EFFORTS} />
        </Field>
        <Switch checked={userChoice} onChange={setUserChoice} label="המשתמש יכול לבחור מודל" />
        {userChoice ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {models.map((m) => (
              <Checkbox
                key={m.id}
                label={m.label}
                checked={allowed.includes(m.id)}
                onChange={(v) => setAllowed((prev) => (v ? [...prev, m.id] : prev.filter((x) => x !== m.id)))}
              />
            ))}
          </div>
        ) : null}
        <Field label="תקציב חודשי לכל הפלטפורמה ($)" hint="ריק = ללא הגבלה; בהגעה לתקציב הסוכן נעצר עד החודש הבא">
          <TextInput ltr inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Platform «AI — מודלים וקרדיטים» (super_admin, ADR-0035): routing per agent, wallets per organization, purchase approvals. */
export function PlatformAiAdmin({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useCkToast();
  const [data, setData] = useState<PlatformAiDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AiFeatureSettingDto | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await aiCreditsApi.platform.overview());
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "לא הצלחנו לטעון את הגדרות ה-AI"));
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const decide = async (p: Purchase, decision: "approve" | "reject") => {
    setDeciding(p.id);
    try {
      await aiCreditsApi.platform.decidePurchase(p.id, decision);
      toast.success(decision === "approve" ? "הרכישה אושרה והקרדיטים נוספו" : "הבקשה נדחתה");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "הפעולה נכשלה"));
    } finally {
      setDeciding(null);
    }
  };

  const modelLabel = (id: string | null) => (id ? (data?.models.find((m) => m.id === id)?.label ?? id) : `ברירת מחדל${data?.defaultModel ? ` (${data.defaultModel})` : ""}`);
  const totalCost = data?.features.reduce((s, f) => s + f.monthCostUsd, 0) ?? 0;

  return (
    <Sheet open={open} onClose={onClose} width={860} ariaLabel="AI — מודלים וקרדיטים">
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <b style={{ flex: 1, fontSize: 18 }}>AI — מודלים וקרדיטים</b>
          <Tag tone="violet">החודש: {fmtUsd(totalCost)}</Tag>
          <IconButton icon="refresh" label="רענון" onClick={() => void load()} />
          <IconButton icon="x" label="סגירה" onClick={onClose} />
        </div>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {data && !data.defaultModel ? <Banner tone="warning">אין מפתח ANTHROPIC_API_KEY בשרת — סוכני ה-AI כבויים.</Banner> : null}

        {data && data.purchases.length ? (
          <Panel title="בקשות רכישה ממתינות" icon="coin" tone="warning">
            <SimpleTable
              ariaLabel="בקשות רכישה"
              rows={data.purchases}
              rowKey={(p: Purchase) => p.id}
              columns={[
                { key: "tenantName", label: "ארגון", render: (p: Purchase) => `${p.tenantName}${p.tenantCode ? ` (${String(p.tenantCode).padStart(6, "0")})` : ""}` },
                { key: "credits", label: "קרדיטים", align: "end", render: (p: Purchase) => fmtCredits(p.credits) },
                { key: "note", label: "הערה", render: (p: Purchase) => p.note ?? "" },
                { key: "createdAt", label: "תאריך", render: (p: Purchase) => `${fmtDate(p.createdAt)} · ${p.requestedByName ?? ""}` },
                {
                  key: "actions",
                  label: "",
                  render: (p: Purchase) => (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <Button size="sm" variant="primary" disabled={deciding === p.id} onClick={() => void decide(p, "approve")}>
                        אישור
                      </Button>
                      <Button size="sm" variant="ghost" disabled={deciding === p.id} onClick={() => void decide(p, "reject")}>
                        דחייה
                      </Button>
                    </span>
                  ),
                },
              ]}
            />
          </Panel>
        ) : null}

        <Panel title="סוכנים — מודל, מאמץ ותקציב" icon="sparkle">
          <SimpleTable
            ariaLabel="סוכני AI"
            loading={!data}
            rows={data?.features ?? []}
            rowKey={(f: AiFeatureSettingDto) => f.feature}
            onRowClick={(f: AiFeatureSettingDto) => setEditing(f)}
            columns={[
              { key: "label", label: "סוכן" },
              {
                key: "enabled",
                label: "מצב",
                render: (f: AiFeatureSettingDto) => <Tag tone={f.enabled ? "success" : "neutral"}>{f.enabled ? "פעיל" : "כבוי"}</Tag>,
              },
              { key: "model", label: "מודל", render: (f: AiFeatureSettingDto) => modelLabel(f.model) },
              { key: "effort", label: "מאמץ", render: (f: AiFeatureSettingDto) => EFFORTS.find((e) => e.value === (f.effort ?? ""))?.label ?? f.effort },
              {
                key: "budget",
                label: "תקציב חודשי",
                align: "end",
                render: (f: AiFeatureSettingDto) => (f.monthlyBudgetUsd === null ? "—" : fmtUsd(f.monthlyBudgetUsd)),
              },
              { key: "monthRequests", label: "פניות", align: "end", render: (f: AiFeatureSettingDto) => f.monthRequests.toLocaleString("he-IL") },
              { key: "monthCostUsd", label: "עלות החודש", align: "end", render: (f: AiFeatureSettingDto) => fmtUsd(f.monthCostUsd) },
            ]}
          />
          <small style={{ color: "var(--ck-text-3)" }}>לחיצה על שורה פותחת עריכה.</small>
        </Panel>

        <Panel title="ארגונים — ארנקי קרדיטים" icon="wallet">
          <SimpleTable
            ariaLabel="ארנקי AI"
            loading={!data}
            rows={data?.tenants ?? []}
            rowKey={(t: Tenant) => t.tenantId}
            maxHeight={360}
            columns={[
              { key: "name", label: "ארגון", render: (t: Tenant) => `${t.name} (${String(t.code).padStart(6, "0")})` },
              { key: "enabled", label: "AI", render: (t: Tenant) => <Tag tone={t.enabled ? "success" : "neutral"}>{t.enabled ? "פעיל" : "כבוי"}</Tag> },
              { key: "allowance", label: "הקצאה", align: "end", render: (t: Tenant) => fmtCredits(t.monthlyAllowanceCredits) },
              { key: "used", label: "נוצל", align: "end", render: (t: Tenant) => fmtCredits(t.usedCredits) },
              { key: "purchased", label: "נרכשו", align: "end", render: (t: Tenant) => fmtCredits(t.purchasedCredits) },
              { key: "cost", label: "עלות החודש", align: "end", render: (t: Tenant) => fmtUsd(t.monthCostUsd) },
            ]}
          />
          <small style={{ color: "var(--ck-text-3)" }}>הקצאה, הענקה וכיבוי לארגון — בכרטיס הארגון, לשונית «AI».</small>
        </Panel>
      </div>
      {editing && data ? (
        <FeatureDialog
          feature={editing}
          models={data.models}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </Sheet>
  );
}
